/**
 * Sparse protocol JSON for create/submit/edit job APIs.
 * Empty sections become `{}`; only filled fields are kept.
 */

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  value != null && typeof value === 'object' && !Array.isArray(value);

const isEmptyString = (value: unknown): boolean =>
  typeof value === 'string' && value.trim().length === 0;

/**
 * Recursively drops null/undefined, blank strings, empty arrays,
 * and empty objects. Returns undefined when the value should be omitted.
 */
export const pruneSparseValue = (value: unknown): unknown => {
  if (value == null || isEmptyString(value)) {
    return undefined;
  }

  if (Array.isArray(value)) {
    const pruned = value
      .map(pruneSparseValue)
      .filter((item): item is NonNullable<typeof item> => item !== undefined);
    return pruned.length > 0 ? pruned : undefined;
  }

  if (isPlainObject(value)) {
    const result: Record<string, unknown> = {};
    for (const [key, child] of Object.entries(value)) {
      const pruned = pruneSparseValue(child);
      if (pruned !== undefined) {
        result[key] = pruned;
      }
    }
    return Object.keys(result).length > 0 ? result : undefined;
  }

  return value;
};

/** Always returns an object — `{}` when nothing remains after pruning. */
export const toSparseObject = (value: unknown): Record<string, unknown> => {
  const pruned = pruneSparseValue(value);
  if (pruned == null || !isPlainObject(pruned)) {
    return {};
  }
  return pruned;
};
